import React, {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector} from "../../Recoil/Atom/authState";

import * as userApi from "../../api/userApi/userApi";

import {useCreateMutation, useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {
  ResponseFile,
  ResponseList,
  ResponseRemove,
} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  Box,
  Button,
  Link,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {alertStateAtom, extraAlertStateAtom} from "../../Recoil/Atom/AlertAtom";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {EQUser, initializeUserListParams, userListParams} from "../../models/serverModels/EQUser";
import {
  StyledButton,
  StyledButtonGroup,
  SubMenuGroup,
} from "../../styledComponents/StyledListContainer";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {ReactComponent as ExcelIcon} from "../../assets/images/excel.svg";

import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";
const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1200px",
}));

const OfficerList = () => {
  const tokenState = useRecoilValue(tokenSelector);

  const [extraAlertState, setExtraAlertState] = useRecoilState(extraAlertStateAtom);

  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<userListParams>({
    ...initializeUserListParams(),
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAlertState = useSetRecoilState(alertStateAtom);
  const {departmentList} = useDepartmentList();

  const {
    data: OfficerList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EQUser>>({
    key: ["OfficerList", {...filter, token: tokenState}],
    api: () => userApi.getList({...filter, token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: userApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("OfficerList");
      },
    },
  });

  const {mutate: loadMutate, data} = useCreateMutation<ResponseFile, Params.ImportFile>({
    api: userApi.importUsers,
    options: {
      onSuccess: (data: ResponseFile) => {
        setExtraAlertState({...extraAlertState, toggle: true, message: data.extra});
      },
      onError: () => {
        setExtraAlertState({...extraAlertState, toggle: true, message: data?.extra!});
      },
    },
  });

  const {control, reset, setValue} = useForm<userListParams>({
    defaultValues: initializeUserListParams(),
  });

  const userListFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const resetHandler = () => {
    setFilter(initializeUserListParams());
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="담당자 목록" />
      <SubMenuGroup>
        <Box display="flex" alignItems="center" gap="0 6px">
          <Controller
            name="searchWord"
            control={control}
            render={({field}) => (
              <TextField
                {...field}
                label="이름 및 부서"
                size="small"
                placeholder="이름 및 부서로 검색"
                onChange={userListFilteringHandler}
              />
            )}
          />
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </Box>
        <StyledButtonGroup>
          <StyledButton
            variant="outlined"
            size="medium"
            color="primary"
            startIcon={<LibraryAddIcon />}
            sx={{whiteSpace: "nowrap", flexShrink: 0}}
            onClick={() => navigate("/Officer/Create")}
          >
            새로추가
          </StyledButton>
          <StyledButton
            variant="outlined"
            size="medium"
            color="success"
            startIcon={<ExcelIcon width="20px" height="20px" />}
            sx={{whiteSpace: "nowrap", flexShrink: 0}}
          >
            <Link
              href="/Downloads/Equipment/Sample/설비담당자양식-샘플.xlsx"
              style={{textDecoration: "none"}}
            >
              Excel 양식받기
            </Link>
          </StyledButton>
          <Button
            variant="outlined"
            size="medium"
            component="label"
            startIcon={<ExcelIcon width="20px" height="20px" />}
            color="success"
            sx={{whiteSpace: "nowrap", flexShrink: 0, fontSize: "12px"}}
          >
            Excel 불러오기
            <TextField
              sx={{display: "none"}}
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                const convertBase64 = (data: Blob): Promise<any> => {
                  return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(data);
                    reader.onload = () => {
                      resolve(reader.result);
                    };
                    reader.onerror = (error) => {
                      reject(error);
                    };
                  });
                };
                const file = event.target?.files![0];
                const base64 = await convertBase64(file);
                loadMutate({token: tokenState, base64: base64.split(",")[1]});
              }}
              type="file"
              id="base64"
              inputProps={{
                accept:
                  ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
              }}
            />
          </Button>
        </StyledButtonGroup>
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">부서명</TableCell>
              <TableCell align="center">이름</TableCell>
              <TableCell align="center">이메일</TableCell>
              <TableCell align="center">아이디</TableCell>
              <TableCell align="center">비밀번호</TableCell>
              <TableCell align="center">작성날짜</TableCell>
              <TableCell align="center">수정날짜</TableCell>
              <TableCell align="center" width="15%">
                편집
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={9} />
            ) : (
              OfficerList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initializeUserListParams().rows! + index + 1}
                  </TableCell>
                  <TableCell align="center">
                    {departmentList?.items?.map((value) =>
                      value.uid === item.departmentUID ? value?.name : ""
                    )}
                  </TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">{item.email}</TableCell>
                  <TableCell align="center">{item.loginID}</TableCell>
                  <TableCell align="center">{item.loginPW}</TableCell>
                  <TableCell align="center">
                    {new Date(item?.registerDate).getFullYear()}년
                    {new Date(item?.registerDate).getMonth() + 1}월
                    {new Date(item?.registerDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">
                    {new Date(item?.modifyDate).getFullYear()}년
                    {new Date(item?.modifyDate).getMonth() + 1}월
                    {new Date(item?.modifyDate).getDate()}일
                  </TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Officer/Edit/${item.uid}`);
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 담당자를 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid!}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={OfficerList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default OfficerList;
